import { apiClient } from './api';

export type GreeningStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'retrying'
  | 'blocked_by_security'
  | 'insufficient_context'
  | 'rate_limited';

export interface GreeningFiveWhys {
  for_government?: string;
  for_company?: string;
  present_changes?: string;
  past_changes?: string;
  future_changes?: string;
}

export interface GreeningAnalysis {
  legislation_document_id: number;
  status: GreeningStatus;
  user_message?: string;
  analysis_text?: string | null;
  five_whys?: GreeningFiveWhys;
  five_whys_status?: GreeningStatus | string;
  five_whys_message?: string;
  five_whys_version?: number;
  controls?: string[];
  demands?: string[];
  sanctions?: string[];
  effective_date?: string | null;
  job_id?: string | null;
  disclaimer?: string;
  error_message?: string | null;
}

export interface GreeningQuestionItem {
  id: number;
  question: string;
  answer?: string | null;
  status: GreeningStatus;
  created_at?: string | null;
  answered_at?: string | null;
  show_oaas_cta?: boolean;
  scope_redirect?: boolean;
  rate_limited?: boolean;
}

export interface GreeningLearning {
  legislation_document_id: number;
  items: Array<{ topic: string; summary: string; frequency: number; risk_level: string }>;
  anonymized_summary?: string | null;
  disclaimer?: string;
}

export const greeningApi = {
  async getAnalysis(legislationId: number): Promise<GreeningAnalysis> {
    const { data } = await apiClient.get(`/api/greening/analysis/${legislationId}`);
    return data;
  },

  async triggerAnalysis(legislationId: number, force = false): Promise<unknown> {
    const { data } = await apiClient.post(`/api/greening/analyze-document/${legislationId}`, null, {
      params: { force },
    });
    return data;
  },

  async ask(legislationId: number, question: string): Promise<{
    job_id?: string;
    status: string;
    user_message?: string;
    answer?: string;
    question_id?: number;
  }> {
    const { data } = await apiClient.post('/api/greening/ask', {
      legislation_document_id: legislationId,
      question,
    });
    return data;
  },

  async getHistory(legislationId: number): Promise<GreeningQuestionItem[]> {
    const { data } = await apiClient.get(`/api/greening/history/${legislationId}`);
    return data.data || [];
  },

  async getLearning(legislationId: number): Promise<GreeningLearning> {
    const { data } = await apiClient.get(`/api/greening/learning/${legislationId}`);
    return data;
  },

  async getJobStatus(jobId: string): Promise<{ status: GreeningStatus; user_message?: string }> {
    const { data } = await apiClient.get(`/api/greening/status/${jobId}`);
    return data;
  },
};

export default greeningApi;
